import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GENERAL_ERROR, generatingMessage, IDLE, LOADING } from "@constants";
import { getFromServerPublicTopic, getFromServerTopic } from "@utility/slice";

const SLICE = "spacesReport";

const action = (name) => `iaqTrends/${name}`;

const getFromServerPublic = (action, contextOptionsFn, payloadFn) =>
  getFromServerPublicTopic({
    topic: "iaqTrends",
    action,
    contextOptionsFn,
    payloadFn,
  });

const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({
    topic: `iaqTrends`,
    action,
    contextOptionsFn,
    payloadFn,
  });

export const loadSpacesReportData = getFromServerPublic(
  "loadSpacesReportData",
  ({ arg }) => arg,
);

export const generateSpaceReports = getFromServer(
  "generateSpaceReports",
  ({ arg }) => arg,
);

export const initializePage = createAsyncThunk(
  action("initializeGenerateSpacesReportPage"),
  async (siteId, { getState }) => {
    const appData = getState().appData;
    const forBuilding = appData.buildingData[siteId];
    const deduper = new Set();
    const spaces = [];
    forBuilding.datapoints.IAQMS.forEach(
      ({
        eid: spaceId,
        floor,
        space,
        space_type: spaceType,
        weekdayStart,
        weekdayEnd,
        saturdayStart,
        saturdayEnd,
        sundayStart,
        sundayEnd,
      }) => {
        if (deduper.has(spaceId)) return;

        spaces.push({
          spaceId,
          floor,
          space,
          spaceType,
          weekdayStart,
          weekdayEnd,
          saturdayStart,
          saturdayEnd,
          sundayStart,
          sundayEnd,
        });

        deduper.add(spaceId);
      },
    );
    return Promise.resolve({ spaces });
  },
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildingData: {},
    reportData: {},
  },
  reducers: {
    selectIndex: (state, action) => {
      const { index, siteId } = action.payload;
      state.buildingData[siteId].index = index;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializePage.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.buildingData[siteId] = { status: LOADING };
      })
      .addCase(initializePage.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const siteId = meta.arg;
        const { spaces } = payload;

        const forBuilding = state.buildingData[siteId];
        forBuilding.spaces = spaces;
        forBuilding.status = IDLE;
      })
      .addCase(generateSpaceReports.fulfilled, (state, action) => {
        const { meta } = action;
        const { siteId } = meta.arg;

        const forBuilding = state.buildingData[siteId];
        forBuilding.errorMessage = "";
        forBuilding.message = generatingMessage("report");
        forBuilding.status = IDLE;
      })
      .addCase(generateSpaceReports.rejected, (state, action) => {
        const { meta } = action;
        const { siteId } = meta.arg;

        const forBuilding = state.buildingData[siteId];
        forBuilding.message = "";
        forBuilding.errorMessage = GENERAL_ERROR;
        forBuilding.status = IDLE;
      })
      .addCase(loadSpacesReportData.pending, (state, action) => {
        const { reportId } = action.meta.arg;
        state.reportData[reportId] = undefined;
      })
      .addCase(loadSpacesReportData.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { reportId } = meta.arg;
        state.reportData[reportId] = payload.response;
      });
  },
});

export const { selectIndex } = slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
